import React, { useCallback, useEffect, useRef, useState } from 'react';

import { analyticsProps } from '@class-ui/insights';
import { CheckCircle, ExpandLess, ExpandMore, Report, WarningAmber } from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    CardContent,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    type DialogProps,
    DialogTitle,
    Divider,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    styled,
    Typography
} from '@mui/material';
import axios from 'axios';
import { isArray } from 'lodash';
import { useTranslation } from 'react-i18next';

import { GET_LMS_AUTH, GET_LMS_SYNC_RESULTS } from '../../../config/apiPaths';
import { type LmsSyncSettings } from '../../../types/apiTypes';
import { getUserUuidHeaders } from '../../../utils/httpUtils';
import { getUrls, processApiPath } from '../../../utils/url';
import { IconTooltip } from '../../common/Tooltip/IconTooltip';
import { StyledTab, StyledTabs } from '../../templates/Tables/index.styles';
import {
    type LmsSyncRequests,
    type LmsSyncResponse,
    type LmsSyncResponseError,
    LmsSyncResult,
    type LmsSyncResultsResponse,
    type ResponseMessagesProps
} from '../LmsSync/LmsSyncDialog.types';
import { CenteredStack, CircularProgressSpaced, NoRecords } from '../SharedComponents';

export type LmsSyncDialogProps = DialogProps & {
    userUuid: string;
    lmsType: string;
    onComplete: () => Promise<void>;
    lmsSyncSettings?: LmsSyncSettings;
    classUuid: string;
    // either the ltiUuid or classId must be provided
    ltiUuid?: string;
    classId?: string;
};

const SectionBox = styled(Box)`
    display: flex;
    flex-wrap: nowrap;
    flex-direction: column;
    padding-bottom: 8px;
`;

const SectionHeader = styled(Box)`
    display: flex;
    align-items: center;
    height: 40px;
    flex-wrap: nowrap;
    flex-direction: row;
    justify-content: center;
`;

const StyledDialog = styled(Dialog)`
    & .MuiDialog-paper {
        min-width: 480px;
        max-width: 800px;
    }
` as typeof Dialog;

enum SelectedTab {
    Sync = 0,
    History = 1
}

const ResponseMessages = (props: ResponseMessagesProps) => {
    const { response, name } = props;
    const { t } = useTranslation('lms_sync');

    if (!response) {
        return (
            <Typography variant="body2" color="text.secondary" key={`lms-sync-${name}-no-content`}>
                {t('no-response')}
            </Typography>
        );
    }

    const errResponse = response as LmsSyncResponseError;
    if (errResponse.error) {
        return (
            <Typography variant="body2" color="error" key={`lms-sync-${name}-err-msg-simple`}>
                {t(errResponse.error)}
            </Typography>
        );
    }

    const succResponse = response as LmsSyncResponse;
    if (!succResponse.status.success && !succResponse.errors.length) {
        succResponse.errors = ['unknown-error'];
    }

    const mapMessages = (messages: string[] | string[][], type: string, color?: string) =>
        messages?.map((message: string | string[], index: number) => {
            const resourceKey = `lms-sync-${name}-${type}-${index}`;
            if (isArray(message)) {
                return (
                    <Typography variant="body2" key={resourceKey} color={color}>
                        {message.map((r) => t(r)).join('')}
                    </Typography>
                );
            }
            if (message === 'auth-failed') {
                return (
                    <CenteredStack>
                        <WarningAmber color="info" />

                        <Typography
                            variant="body2"
                            color="text.secondary"
                            key={`lms-sync-${name}-err-msg`}
                            align="center"
                        >
                            {t('auth-failed')}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            key={`lms-sync-${name}-err-msg-details`}
                            align="center"
                        >
                            {t('auth-failed-details')}
                        </Typography>
                    </CenteredStack>
                );
            }
            return (
                <Typography variant="body2" key={resourceKey} color={color}>
                    {t(message)}
                </Typography>
            );
        });

    const messages = mapMessages(succResponse!.messages, 'msg');
    const errors = mapMessages(succResponse!.errors, 'err-msg', 'error');

    return <>{[...errors, ...messages]}</>;
};

type expandedState = {
    [key: number]: boolean;
};

export const LmsSyncDialog = (props: LmsSyncDialogProps) => {
    const { ltiUuid, classId, classUuid, lmsType, userUuid, onClose, onComplete, lmsSyncSettings } = props;
    const { t } = useTranslation('lms_sync');
    const [selectedTab, setSelectedTab] = useState<SelectedTab>(SelectedTab.Sync);
    const contentRef = useRef<HTMLDivElement | null>(null);

    const [requests, setRequests] = useState<LmsSyncRequests | null>(null);
    const [syncHistory, setSyncHistory] = useState<LmsSyncResultsResponse | null>(null);
    const [syncHistoryLoading, setSyncHistoryLoading] = useState<boolean>(false);
    const [syncHistoryExpanded, setSyncHistoryExpanded] = useState<expandedState>({});

    const handleClose = useCallback(
        (event) => {
            onClose?.(event, 'backdropClick');
        },
        [onClose]
    );

    useEffect(() => {
        if (!lmsType || requests) return;

        const buildState = () => {
            const parts = [`user_uuid:${userUuid}`];
            if (ltiUuid) {
                parts.push(`lti_uuid:${ltiUuid}`);
            }
            parts.push(`class_id:${classId}`);
            return parts.join('/');
        };

        const state = buildState();
        const { api } = getUrls();

        const buildUrl = (apiCallName: string) =>
            `${api}/v1/lti/${lmsType?.toLowerCase()}/${apiCallName}?state=${state}`;

        setRequests({
            course: {
                url: buildUrl('lms_sync_course'),
                title: t('course-sync-title'),
                hint: t('course-sync-hint'),
                enabled: true,
                fetched: false,
                inProgress: false
            },
            roster: {
                url: buildUrl('lms_sync_roster'),
                title: t('roster-sync-title'),
                enabled: false,
                fetched: false,
                inProgress: false
            },
            contents: {
                url: buildUrl('lms_sync_contents'),
                title: t('content-sync-title'),
                enabled: false,
                fetched: false,
                inProgress: false
            },
            attendance_grade: {
                url: buildUrl('lms_sync_attendance_grade'),
                title: t('attendance-grade-sync-title'),
                enabled: false,
                fetched: false,
                inProgress: false
            },
            attendance: {
                url: buildUrl('lms_sync_attendance_to_lms'),
                title: t('attendance-to-lms-sync-title'),
                enabled: false,
                fetched: false,
                inProgress: false
            },
            sessions: {
                url: buildUrl('lms_sync_class_sessions'),
                title: t('sessions-sync-title'),
                enabled: false,
                fetched: false,
                inProgress: false
            }
        });
    }, [t, userUuid, ltiUuid, classId, lmsType, requests]);

    useEffect(() => {
        if (contentRef.current) {
            contentRef.current.scrollTop = contentRef.current.scrollHeight;
        }
    }, [requests]);

    useEffect(() => {
        if (lmsSyncSettings) {
            setRequests((current) => {
                if (!current) return current;

                const updatedRequests = { ...current };
                updatedRequests.roster.enabled = Boolean(lmsSyncSettings.enable_roster);
                updatedRequests.contents.enabled = Boolean(lmsSyncSettings.enable_content);
                updatedRequests.attendance_grade.enabled = Boolean(lmsSyncSettings.enable_attendance_grade);
                updatedRequests.sessions.enabled = Boolean(lmsSyncSettings.enable_sessions);
                updatedRequests.attendance.enabled = Boolean(lmsSyncSettings.enable_attendance);
                return updatedRequests;
            });
        }
    }, [lmsSyncSettings]);

    const triggerLmsAuth = useCallback(() => {
        // give the user a chance to read the messages before the new tab opens
        setTimeout(
            () =>
                window.open(
                    processApiPath(GET_LMS_AUTH, { lmsType }, { state: `user_uuid:${userUuid}/lti_uuid:${ltiUuid}` }),
                    '_blank'
                ),
            3000
        );
    }, [lmsType, userUuid, ltiUuid]);

    const fetchData = useCallback(
        async (url: string, key: string): Promise<LmsSyncResult> => {
            console.log('Syncing Key', { key, url, enabled: requests?.[key].enabled });
            if (!requests?.[key].enabled) {
                return LmsSyncResult.Skipped;
            }

            setRequests((current) =>
                current
                    ? {
                          ...current,
                          [key]: {
                              ...current[key],
                              inProgress: true
                          }
                      }
                    : current
            );
            let response: LmsSyncResponse;
            try {
                const { data } = await axios.get<LmsSyncResponse>(url, {
                    headers: getUserUuidHeaders(userUuid)
                });
                response = data;
            } catch (error: any) {
                console.error('error', error);
                if (error.response?.data?.status?.result) {
                    response = error.response.data;
                } else {
                    response = {
                        status: {
                            success: false,
                            result: LmsSyncResult.Failed
                        },
                        errors: [error.message],
                        messages: []
                    };
                }
            }
            setRequests((current) =>
                current
                    ? {
                          ...current,
                          [key]: {
                              ...current[key],
                              inProgress: false,
                              fetched: true,
                              response
                          }
                      }
                    : current
            );

            return response.status?.result;
        },
        [requests, userUuid]
    );

    const fetchHistory = useCallback(async () => {
        setSyncHistoryLoading(true);
        try {
            const { data } = await axios.get(processApiPath(GET_LMS_SYNC_RESULTS, { classUuid }), {
                headers: getUserUuidHeaders(userUuid)
            });
            setSyncHistoryExpanded({});
            setSyncHistory(data);
        } catch (error) {
            console.error('Failed to fetch history:', error);
            setSyncHistoryExpanded({});
            setSyncHistory({ results: [] });
        }
        setSyncHistoryLoading(false);
    }, [classUuid, userUuid]);

    const onClickHistoryExpand = useCallback(
        (index: number) => {
            setSyncHistoryExpanded((current) => {
                const updated = { ...current };
                updated[index] = !updated[index];
                return updated;
            });
        },
        [setSyncHistoryExpanded]
    );

    const onClickLaunch = useCallback(async () => {
        if (!requests) return;

        setRequests((current) => {
            if (!current) return current;

            const updatedRequests = { ...current };
            Object.keys(updatedRequests).forEach((key) => {
                delete updatedRequests[key].response;
                updatedRequests[key].fetched = false;
            });
            return updatedRequests;
        });

        // synchronously fetch course data - gathered info may be required for further requests
        const courseResult = await fetchData(requests.course.url, 'course');
        switch (courseResult) {
            case LmsSyncResult.Unauthorized:
                triggerLmsAuth();
                return;
            case LmsSyncResult.Failed:
                console.error('Failed to sync course');
                return;
            default:
                break;
        }

        // Do one sync at a time; concurrent requests have been seen to cause LMS
        // requests to hang
        const results: LmsSyncResult[] = [];
        for (const key of ['roster', 'contents', 'sessions']) {
            const result = await fetchData(requests[key].url, key);
            results.push(result);
            if (result === LmsSyncResult.Unauthorized) {
                triggerLmsAuth();
                return;
            }
        }

        if (requests.attendance_grade?.enabled) {
            await fetchData(requests.attendance_grade.url, 'attendance_grade');
        }

        if (requests.attendance?.enabled) {
            await fetchData(requests.attendance.url, 'attendance');
        }

        Promise.all([fetchHistory(), onComplete()]);
    }, [requests, fetchData, fetchHistory, onComplete, triggerLmsAuth]);

    const inProgress: boolean = Boolean(requests && Object.values(requests).find((value) => value.inProgress));
    const haveContent = requests && (inProgress || Object.values(requests).find((value) => Boolean(value.fetched)));

    const syncButton = (
        <Button
            size="medium"
            color="primary"
            variant="contained"
            onClick={onClickLaunch}
            disabled={inProgress}
            {...analyticsProps('lms-sync-dialog-button-trigger')}
        >
            {t('trigger-sync')}
        </Button>
    );

    useEffect(() => {
        if (selectedTab === SelectedTab.History && syncHistory === null && !syncHistoryLoading) {
            fetchHistory();
        }
    }, [fetchHistory, selectedTab, syncHistory, syncHistoryLoading]);

    const getIconForSyncResult = (result: LmsSyncResult) => {
        switch (result) {
            case LmsSyncResult.Synced:
                return <CheckCircle color="success" />;
            default:
                return <Report color="error" />;
        }
    };

    return (
        <StyledDialog open={props.open} onClose={handleClose}>
            <DialogTitle {...analyticsProps('lms-sync-dialog-title')}>{t('syncing-with-lms')}</DialogTitle>

            <DialogContent ref={contentRef}>
                <StyledTabs value={selectedTab} onChange={(_event, newValue: number) => setSelectedTab(newValue)}>
                    <StyledTab label={t('sync-tabs.sync')} />
                    <StyledTab label={t('sync-tabs.history')} />
                </StyledTabs>
                {selectedTab === SelectedTab.Sync && (
                    <>
                        {haveContent ? (
                            Object.entries(requests)?.map(([name, request], index: number) =>
                                request.enabled && (request.inProgress || request.fetched) ? (
                                    <SectionBox key={`request-content-${name}`}>
                                        {index > 0 && <Divider key={`divider-${name}`} />}
                                        <SectionHeader>
                                            <Typography variant="body1">{request.title}</Typography>
                                            {request.hint && <IconTooltip>{request.hint}</IconTooltip>}
                                        </SectionHeader>
                                        {request.inProgress ? (
                                            <CircularProgressSpaced spacing="xsmall" />
                                        ) : (
                                            <ResponseMessages response={request.response!} name={name} />
                                        )}
                                    </SectionBox>
                                ) : null
                            )
                        ) : (
                            <NoRecords message={t('no-manual-sync-yet')}>{syncButton}</NoRecords>
                        )}
                    </>
                )}
                {selectedTab === SelectedTab.History && (
                    <>
                        {syncHistoryLoading && <CircularProgressSpaced />}

                        {!syncHistoryLoading && syncHistory?.results.length === 0 ? (
                            <NoRecords message={t('history.no-sync-history-yet')} />
                        ) : (
                            <List>
                                {syncHistory?.results
                                    .map((result, index) => [
                                        <ListItemButton
                                            key={`history-result-item-${index}`}
                                            onClick={() => onClickHistoryExpand(index)}
                                        >
                                            <ListItemIcon>{getIconForSyncResult(result.result)}</ListItemIcon>
                                            <ListItemText
                                                primary={`${t(`history.source-${result.source}`)} - ${t(`history.type-${result.type}`)}`}
                                                secondary={`${t(`history.result-${result.result}`)} - ${new Date(result.created_at).toLocaleString()}`}
                                            />
                                            {Boolean(syncHistoryExpanded[index]) ? <ExpandLess /> : <ExpandMore />}
                                        </ListItemButton>,
                                        <Collapse
                                            in={Boolean(syncHistoryExpanded[index])}
                                            timeout="auto"
                                            key={`history-collapse-${index}`}
                                            unmountOnExit
                                        >
                                            <Card>
                                                <CardContent>
                                                    <ResponseMessages
                                                        response={result.data}
                                                        name={t(`history.type-${result.type}`)}
                                                    />
                                                </CardContent>
                                            </Card>
                                        </Collapse>
                                    ])
                                    .flat()}
                            </List>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button
                    size="medium"
                    color="primary"
                    variant="outlined"
                    onClick={handleClose}
                    {...analyticsProps('lms-sync-dialog-button-close')}
                >
                    {t('common:close')}
                </Button>
                {haveContent && selectedTab === SelectedTab.Sync && syncButton}
            </DialogActions>
        </StyledDialog>
    );
};
